import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "../../../components/DashboardLayout";
import {
  formatDate,
  formatDate2,
  formatTime,
  getDayFromDate,
  getTodayDate,
  scheduleDate,
  scheduleTime,
} from "../../../utility/Common";
import Loader from "../../../components/Loader";
import { getClassesAPI } from "../../../services/portal/admin/classService";
import { getPopupBannerAPI } from "../../../services/portal/admin/bannerService";
import { showErrorToastMsg } from "../../../utility/ToastMessage";
import {
  getMyPaidTodayClassesAPI,
  getStudentClassScheduleAssignsAPI,
} from "../../../services/portal/student/studentService";
import { TimeIcon } from "../../../icons/TimeIcon";
import { CalendarIcon } from "../../../icons/CalendarIcon";
import { RoutePaths } from "../../../utility/RoutePaths";
import { useAuth } from "../../../contexts/AuthContext";

const StudentDashboardPage = () => {
  const navigate = useNavigate();
  const { user, isBirthday } = useAuth();
  const [showBirthdayBanner, setShowBirthdayBanner] = useState(isBirthday);
  const [loading, setLoading] = useState(false);
  const [assignedClassesLoading, setAssignedClassesLoading] = useState(false);
  const [classes, setClasses] = useState([]);
  const [paidClasses, setPaidClasses] = useState([]);
  const [assignedClasses, setAssignedClasses] = useState([]);
  const [popupBanner, setPopupBanner] = useState(null);
  const [showPopup, setShowPopup] = useState(false);

  useEffect(() => {
    getMyPaidTodayClasses();
    getStudentClassScheduleAssigns();
    loadClasses();
    loadPopupBanner();
  }, []);

  const loadPopupBanner = async () => {
    // if (sessionStorage.getItem("popupBannerShown")) return;
    try {
      const res = await getPopupBannerAPI(1);
      if (res && res.IsSuccessful && res.Data) {
        const raw = res.Data;
        const banner = Array.isArray(raw) ? raw[0] : raw;
        if (banner && (banner.Status ?? banner.status) === 1) {
          setPopupBanner(banner);
          setShowPopup(true);
          sessionStorage.setItem("popupBannerShown", "1");
        }
      }
    } catch (error) {
      console.error("Error loading popup banner:", error);
    }
  };

  const closePopup = () => setShowPopup(false);

  const loadClasses = async () => {
    setLoading(true);
    try {
      const res = await getClassesAPI();
      if (res && res.IsSuccessful && res.Data) {
        setClasses(res.Data);
      }
    } catch (error) {
      console.error("Error loading classes:", error);
      showErrorToastMsg("Failed to load classes");
    } finally {
      setLoading(false);
    }
  };

  const getMyPaidTodayClasses = async () => {
    setLoading(true);
    try {
      const res = await getMyPaidTodayClassesAPI(getTodayDate(1));
      var list = [];
      if (res && res.IsSuccessful && res.Data) {
        res.Data.map((classItem) => {
          classItem.ClassSchedules.map((item) => {
            item.ClassName = classItem.Name;
            item.ClassId = classItem.ClassId;
            list.push(item);
          });
        });
        setPaidClasses(list);
      }
    } catch (error) {
      console.error("Error loading classes:", error);
      showErrorToastMsg("Failed to load classes");
    } finally {
      setLoading(false);
    }
  };

  const getStudentClassScheduleAssigns = async () => {
    setAssignedClassesLoading(true);
    try {
      const res = await getStudentClassScheduleAssignsAPI(getTodayDate(1));
      var list = [];
      if (res && res.IsSuccessful && res.Data) {
        res.Data.map((classItem) => {
          var item = {
            ClassName: classItem.Name,
            ClassId: classItem.ClassId,
            ExpireDate: classItem.Date,
            ...classItem.ClassSchedule,
          };
          list.push(item);
        });
        setAssignedClasses(list);
      }
    } catch (error) {
      console.error("Error loading classes:", error);
      showErrorToastMsg("Failed to load classes");
    } finally {
      setAssignedClassesLoading(false);
    }
  };

  const handleCardClick = (classId, e) => {
    // Don't navigate if clicking on the button
    if (e.target.tagName === "BUTTON" || e.target.closest("button")) {
      return;
    }
    navigate(`${RoutePaths.STUDENT_CLASS_DETAILS_PAGE}?classId=${classId}`);
  };

  const openScheduleDetails = (scheduleItem) => {
    navigate(
      `${RoutePaths.STUDENT_CLASS_SCHEDULE_DETAILS_PAGE}?classId=${scheduleItem.ClassId}&scheduleId=${scheduleItem.ClassScheduleId}`,
    );
  };

  const renderClassCard = (classItem) => {
    const classId = classItem.ClassId;

    return (
      <div
        key={classId}
        onClick={(e) => handleCardClick(classId, e)}
        className={`bg-white rounded-lg shadow-md p-6 transition duration-300 border-l-4 cursor-pointer border-primary-600 hover:shadow-xl`}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-gray-900">{classItem.Name}</h3>
        </div>

        <div className="flex justify-between text-gray-600 mt-4">
          <div className="flex items-center">
            <CalendarIcon />
            <div>
              <p className="font-semibold text-sm">
                {getDayFromDate(classItem.StartDate)}
              </p>
            </div>
          </div>
          <div className="flex items-center">
            <TimeIcon />
            <div>
              <p className="font-semibold text-sm">
                {formatTime(classItem.StartTime) +
                  " - " +
                  formatTime(classItem.EndTime)}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderClassScheduleCard = (classItem) => {
    const classScheduleId = classItem.ClassScheduleId;
    const imgSrc = classItem.Image
      ? classItem.Image.startsWith("data:")
        ? classItem.Image
        : `data:image/jpeg;base64,${classItem.Image}`
      : "/images/no_image.jpg";

    return (
      <div
        key={classScheduleId}
        onClick={(e) => {
          if (e.target.closest("button")) return;
          openScheduleDetails(classItem);
        }}
        className={`group bg-white rounded-xl overflow-hidden shadow-md transition-all duration-300 cursor-pointer hover:shadow-xl hover:-translate-y-1`}
      >
        {/* Image header */}
        <div className="relative h-48 overflow-hidden bg-gray-100">
          <img
            src={imgSrc}
            alt={classItem.ClassName}
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
          <div className="absolute bottom-2 left-3 right-3 flex items-end justify-between">
            <h3 className="text-lg font-bold text-white drop-shadow-sm line-clamp-1">
              {classItem.ClassName}
            </h3>
          </div>
        </div>

        {/* Body */}
        <div className="p-4 space-y-3 h-32">
          {classItem.Description && (
            <p className="text-xl font-bold text-gray-900 line-clamp-2">
              {classItem.Description}
            </p>
          )}
          <div className="flex gap-2 text-gray-700">
            <CalendarIcon />
            <span className="text-sm font-medium">
              {scheduleDate(classItem.StartDate, classItem.EndDate)}
            </span>
          </div>

          <div className="flex gap-2 text-gray-700">
            <TimeIcon />
            <span className="text-sm font-medium">
              {scheduleTime(classItem.StartDate, classItem.EndDate)}
            </span>
          </div>
        </div>

        {classItem.ExpireDate && classItem.ExpireDate.length > 0 && (
          <div className="pl-4 pt-4 flex text-gray-700">
            <span className="text-sm font-medium text-red-600">
              Expire on: {formatDate2(classItem.ExpireDate)}
            </span>
          </div>
        )}

        <div className="p-4 space-y-3">
          <div className="flex justify-end">
            <button
              onClick={(e) => {
                e.stopPropagation();
                openScheduleDetails(classItem);
              }}
              className="w-auto p-5 py-2.5 rounded-lg font-semibold text-sm transition-colors
bg-primary-600 text-white hover:bg-primary-700"
            >
              Join
            </button>
          </div>
        </div>
      </div>
    );
  };

  const popupImageSrc = popupBanner?.Image
    ? popupBanner.Image.startsWith("data:") ||
      popupBanner.Image.startsWith("http")
      ? popupBanner.Image
      : `data:image/jpeg;base64,${popupBanner.Image}`
    : null;

  return (
    <DashboardLayout title="Dashboard" subtitle="Welcome back!">
      {showBirthdayBanner && (
        <div className="relative mb-6 rounded-xl overflow-hidden bg-gradient-to-r from-pink-500 via-red-400 to-yellow-400 px-6 py-4 flex items-center justify-between shadow-md">
          <div className="flex items-center gap-3">
            <span className="text-6xl">🎂</span>
            <div>
              {/* <p className="text-white font-bold text-lg leading-tight">
                Happy Birthday, {user?.FirstName}!
              </p> */}
              <p className="text-white font-bold text-lg ">
                ආදරණීය පුතේ ඔයාට සුභම සුභ උපන්දිනයක් වේවා!
              </p>
              <p className="text-white font-bold text-lg ">
                ඔයාගේ අධ්‍යාපන කටයුතු වගේම අනාගත වැඩ කටයුතු සියල්ලම සාර්ථක වේවා. 
              </p>
              <p className="text-white font-bold text-lg ">
                මීට ඔයාලගේ ආදරණීය හර්ෂ සර්
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowBirthdayBanner(false)}
            className="ml-4 text-white/80 hover:text-white transition-colors"
            aria-label="Dismiss"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      )}
      {loading ? (
        <div className="flex justify-center items-center py-12">
          <Loader />
        </div>
      ) : (
        <>
          {assignedClassesLoading && (
            <div className="flex justify-center items-center py-12">
              <Loader />
            </div>
          )}

          {assignedClasses.length > 0 && (
            <>
              <div className="mb-8">
                <h2 className="text-xl font-bold text-gray-900 mb-4">
                  Temporarily Assigned Classes | තාවකාලිකව පවරා ඇති පන්ති
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-4 xxl:grid-cols-4 gap-6 mb-4">
                  {assignedClasses.map((classItem) =>
                    renderClassScheduleCard(classItem),
                  )}
                </div>
                <hr />
              </div>
            </>
          )}

          {/* Available Classes Section */}
          {paidClasses.length > 0 && (
            <>
              <div className="mb-8">
                <h2 className="text-xl font-bold text-gray-900 mb-4">
                  Paid Classes | ගෙවීම් කර ඇති පන්ති
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-4 xxl:grid-cols-4 gap-6 mb-4">
                  {paidClasses.map((classItem) =>
                    renderClassScheduleCard(classItem),
                  )}
                </div>
                <hr />
              </div>
            </>
          )}

          <div>
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              All Classes | සියලුම පන්ති
            </h2>
            {classes.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-600 text-lg">No classes available</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-3 gap-6">
                {classes.map((classItem) => renderClassCard(classItem))}
              </div>
            )}
          </div>
        </>
      )}
      {/* Popup Banner Modal */}
      {showPopup && popupBanner && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-4xl h-full max-h-[90vh] overflow-y-auto">
            {/* Close button */}
            <button
              onClick={closePopup}
              className="absolute top-3 right-3 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors"
              aria-label="Close"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            {/* Banner image */}
            {popupImageSrc && (
              <div className="w-full">
                <img
                  src={popupImageSrc}
                  alt={popupBanner.Title ?? ""}
                  className="w-full object-contain rounded-t-xl h-full max-h-[65vh]"
                />
              </div>
            )}

            {/* Content */}
            <div className="p-6">
              {(popupBanner.Title ?? popupBanner.title) && (
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {popupBanner.Title ?? popupBanner.title}
                </h3>
              )}
              {(popupBanner.Description ?? popupBanner.description) && (
                <p className="text-gray-600 text-sm mb-4">
                  {popupBanner.Description ?? popupBanner.description}
                </p>
              )}
              <div className="flex justify-end gap-3">
                <button
                  onClick={closePopup}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Close
                </button>
                {popupBanner.URL && (
                  <a
                    href={popupBanner.URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={closePopup}
                    className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    Learn More
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default StudentDashboardPage;
